/**
 * FriendProfileHost — mounted once in the header so ANY friends rail/list/panel
 * can open a friend's profile via:
 *   window.dispatchEvent(new CustomEvent("iw:open-friend-profile", { detail: { targetId } }))
 * Also hosts:
 *  - "Invite to Play" using the REAL Arcade/Art game catalog. It creates a native
 *    game room (the same /game-rooms flow every game uses) and sends the friend a
 *    deep-link that auto-joins by pin — the familiar lobby, just skipping the PIN.
 *  - A friend walkie-talkie (reuses the arcade PushToTalk voice channel) so online
 *    friends can talk without being in a game.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { X, Gamepad2, Loader2, TreePine, Users, Check } from "lucide-react";
import axios from "axios";
import { useApp } from "../context";
import FriendProfileModal from "./FriendProfileModal";
import { useFriendConfig } from "../hooks/useFriendConfig";
import { getStickerEmoji } from "./StickerPicker";
import FriendFace from "./FriendFace";

const API = process.env.REACT_APP_BACKEND_URL;

// Party-capable games: native board games whose online rooms hold 3-4 players
// and rotate extras on the bench (loser out, next in) — exactly the group flow.
const PARTY_GAMES = [
  { name: "Tic-Tac-Toe", type: "tic-tac-toe", route: "/arcade/tic-tac-toe", icon: "❌", max: 4 },
  { name: "Connect Four", type: "connect-four", route: "/arcade/connect-four", icon: "🔴", max: 4 },
  { name: "Checkers", type: "checkers", route: "/arcade/checkers", icon: "🏁", max: 4 },
];

// Only games that support online 2-player via the shared GameShell (?pin auto-join).
const CATALOG = {
  arcade: [
    { name: "Chess", type: "chess", route: "/arcade/chess", icon: "♟️" },
    { name: "Checkers", type: "checkers", route: "/arcade/checkers", icon: "🏁" },
    { name: "Connect Four", type: "connect-four", route: "/arcade/connect-four", icon: "🔴" },
    { name: "Tic-Tac-Toe", type: "tic-tac-toe", route: "/arcade/tic-tac-toe", icon: "❌" },
  ],
  art: [
    { name: "Quick Draw", type: "art-quick-draw", route: "/art-studio/quick-draw-game", icon: "✏️" },
    { name: "Guess It", type: "art-guess", route: "/art-studio/guess-game", icon: "🎨" },
    { name: "Finish the Drawing", type: "art-finish", route: "/art-studio/finish-drawing-game", icon: "🖌️" },
  ],
};

export const FriendProfileHost = () => {
  const { user } = useApp();
  const navigate = useNavigate();
  const [targetId, setTargetId] = useState(null);
  const [inviteFor, setInviteFor] = useState(null);
  const [sending, setSending] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);
  const [groupFriends, setGroupFriends] = useState([]);
  const [groupSelected, setGroupSelected] = useState([]); // friend ids
  const [groupGame, setGroupGame] = useState(PARTY_GAMES[0]);
  const [groupSending, setGroupSending] = useState(false);
  const { config } = useFriendConfig();

  const viewerId = user && (user.id || user.email);
  const hostName = user && (user.display_name || user.first_name || user.name || "Explorer");

  useEffect(() => {
    const handler = (e) => { if (e.detail?.targetId) setTargetId(e.detail.targetId); };
    window.addEventListener("iw:open-friend-profile", handler);
    return () => window.removeEventListener("iw:open-friend-profile", handler);
  }, []);

  // Open the walkie-talkie directly from anywhere (e.g. the Friends chat header).
  // We no longer render our OWN voice card here — that produced a SECOND card
  // stacked on top of the global IncomingVoicePopup and split the conversation.
  // The global popup (App.js) now owns the single, history-carrying thread; the
  // iw:friend-talk event is handled there, so there is nothing to do here.

  // "Switch Game" from a finished multiplayer game: RematchPanel dispatches this
  // with the opponent's id/name; we open the game picker so the host can set up a
  // fresh game and auto-invite the same friend (no PIN, no full restart).
  useEffect(() => {
    const handler = (e) => {
      const oid = e.detail?.opponentId;
      if (oid && oid !== viewerId) setInviteFor(oid);
    };
    window.addEventListener("iw:switch-game", handler);
    return () => window.removeEventListener("iw:switch-game", handler);
  }, [viewerId]);

  // Group Play ("Mini Party"): open a setup where the host picks a party game +
  // a few friends; we create ONE room and auto-invite each friend by pin.
  useEffect(() => {
    const handler = () => {
      setGroupSelected([]); setGroupGame(PARTY_GAMES[0]); setGroupOpen(true);
      axios.get(`${API}/api/friends/${viewerId}/enriched`).then((r) => setGroupFriends(r.data.friends || [])).catch(() => setGroupFriends([]));
    };
    window.addEventListener("iw:start-group-play", handler);
    return () => window.removeEventListener("iw:start-group-play", handler);
  }, [viewerId]);

  const maxParty = Math.min(config.max_group_players || 4, groupGame.max);

  const toggleGroupFriend = (id) => {
    setGroupSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= maxParty - 1) return prev; // host + (maxParty-1) friends
      return [...prev, id];
    });
  };

  const startParty = async () => {
    if (!viewerId || groupSelected.length < 1) return;
    setGroupSending(true);
    try {
      const players = groupSelected.length + 1;
      const r = await axios.post(`${API}/api/game-rooms/create`, {
        user_id: viewerId, user_name: hostName, game_type: groupGame.type, max_players: players,
        is_party: true, party_label: groupGame.name,
      });
      const pin = r.data.pin;
      await Promise.all(groupSelected.map((fid) =>
        axios.post(`${API}/api/friends/play/invite-room`, {
          host_id: viewerId, friend_id: fid, game_name: groupGame.name, route: groupGame.route, pin,
        }).catch(() => {})
      ));
      setGroupOpen(false);
      navigate(`${groupGame.route}?host=true&pin=${pin}`);
    } catch { /* silent */ }
    setGroupSending(false);
  };

  const onMessage = useCallback((id, mode) => {
    setTargetId(null); setInviteFor(null);
    if (mode === "respond") { navigate("/friends?tab=requests"); return; }
    // Navigate for the fresh-load case AND fire an event so an already-mounted
    // FriendsPage opens the chat immediately (navigating to the same URL does
    // not re-trigger its param effect — that was the "nothing happens" bug).
    navigate(`/friends?chat=${id}`);
    window.dispatchEvent(new CustomEvent("iw:open-friend-chat", { detail: { id } }));
  }, [navigate]);

  const onInvite = useCallback((id) => setInviteFor(id), []);
  // Route "Talk" to the single global walkie-talkie popup (shared thread with
  // history) instead of opening a second, separate card here.
  const onTalk = useCallback((id, name) => { setTargetId(null); window.dispatchEvent(new CustomEvent("iw:friend-talk", { detail: { id, name } })); }, []);

  // Create a native game room (same as pressing "Host a Game" in that game),
  // notify the friend with a pin deep-link, then drop the host into the lobby.
  const sendInvite = async (game) => {
    if (!viewerId || !inviteFor) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/api/game-rooms/create`, {
        user_id: viewerId, user_name: hostName, game_type: game.type, max_players: 2,
      });
      const pin = r.data.pin;
      await axios.post(`${API}/api/friends/play/invite-room`, {
        host_id: viewerId, friend_id: inviteFor, game_name: game.name, route: game.route, pin,
      });
      setInviteFor(null); setTargetId(null);
      navigate(`${game.route}?host=true&pin=${pin}`);
    } catch { /* silent */ }
    setSending(false);
  };

  if (!user || user.isDemo || !viewerId) return null;

  return (
    <>
      {targetId && (
        <FriendProfileModal
          viewerId={viewerId}
          targetId={targetId}
          onClose={() => setTargetId(null)}
          onMessage={onMessage}
          onInvite={onInvite}
          onTalk={onTalk}
        />
      )}

      {inviteFor && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4" data-testid="play-invite-picker">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setInviteFor(null)} />
          <div className="relative w-full max-w-lg rounded-3xl border-2 border-violet-500/30 bg-slate-900 p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-white font-fredoka font-bold text-lg flex items-center gap-2"><Gamepad2 size={20} className="text-violet-400" /> Invite to Play</h3>
              <button onClick={() => setInviteFor(null)} className="w-7 h-7 rounded-full bg-white/5 text-white/60 hover:text-white flex items-center justify-center"><X size={16} /></button>
            </div>
            <p className="text-white/50 text-sm mb-4">Pick a game — your friend jumps straight in, no code to type.</p>

            <p className="text-violet-300/80 text-[11px] font-bold uppercase tracking-wider mb-2">Arcade City</p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {CATALOG.arcade.map((g) => (
                <button key={g.type} onClick={() => sendInvite(g)} disabled={sending}
                  className="flex items-center gap-2.5 p-3 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-violet-400/40 transition-all text-left disabled:opacity-50"
                  data-testid={`invite-game-${g.type}`}>
                  <span className="text-2xl">{g.icon}</span>
                  <span className="text-white font-bold text-sm">{g.name}</span>
                </button>
              ))}
            </div>

            <p className="text-pink-300/80 text-[11px] font-bold uppercase tracking-wider mb-2">Art Studio</p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {CATALOG.art.map((g) => (
                <button key={g.type} onClick={() => sendInvite(g)} disabled={sending}
                  className="flex items-center gap-2.5 p-3 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-pink-400/40 transition-all text-left disabled:opacity-50"
                  data-testid={`invite-game-${g.type}`}>
                  <span className="text-2xl">{g.icon}</span>
                  <span className="text-white font-bold text-sm">{g.name}</span>
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2.5 p-3 rounded-2xl bg-white/[0.03] border border-dashed border-white/10 opacity-60" data-testid="invite-game-forest-soon">
              <TreePine size={22} className="text-emerald-400/60" />
              <div className="flex-1">
                <p className="text-white/70 font-bold text-sm">Learning Forest Battle</p>
                <p className="text-white/30 text-[11px]">Head-to-head learning duels</p>
              </div>
              <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase bg-orange-500/15 text-orange-400/80 border border-orange-500/20">Coming Soon</span>
            </div>

            {sending && <div className="flex items-center justify-center gap-2 text-white/50 text-xs mt-4"><Loader2 size={14} className="animate-spin" /> Setting up your game…</div>}
          </div>
        </div>
      )}

      {groupOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4" data-testid="group-play-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setGroupOpen(false)} />
          <div className="relative w-full max-w-lg rounded-3xl border-2 border-fuchsia-500/30 bg-slate-900 p-6 max-h-[88vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-white font-fredoka font-bold text-lg flex items-center gap-2"><Users size={20} className="text-fuchsia-400" /> Mini Party</h3>
              <button onClick={() => setGroupOpen(false)} className="w-7 h-7 rounded-full bg-white/5 text-white/60 hover:text-white flex items-center justify-center" data-testid="group-play-close"><X size={16} /></button>
            </div>
            <p className="text-white/50 text-sm mb-4">Pick a game and up to {maxParty - 1} friends. Everyone jumps into one room — the first two play while the rest wait their turn and rotate in.</p>

            <p className="text-fuchsia-300/80 text-[11px] font-bold uppercase tracking-wider mb-2">Choose a game</p>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {PARTY_GAMES.map((g) => (
                <button key={g.type} onClick={() => setGroupGame(g)}
                  className={`flex flex-col items-center gap-1 p-3 rounded-2xl border transition-all ${groupGame.type === g.type ? "bg-fuchsia-500/20 border-fuchsia-400/50" : "bg-white/5 border-white/10 hover:bg-white/10"}`}
                  data-testid={`party-game-${g.type}`}>
                  <span className="text-2xl">{g.icon}</span>
                  <span className="text-white font-bold text-[11px]">{g.name}</span>
                </button>
              ))}
            </div>

            <p className="text-fuchsia-300/80 text-[11px] font-bold uppercase tracking-wider mb-2">Invite friends ({groupSelected.length}/{maxParty - 1})</p>
            {groupFriends.length === 0 ? (
              <p className="text-white/40 text-sm py-4 text-center">Add some friends first to start a party.</p>
            ) : (
              <div className="space-y-2 mb-4 max-h-[260px] overflow-y-auto">
                {groupFriends.map((f) => {
                  const sel = groupSelected.includes(f.id);
                  const full = !sel && groupSelected.length >= maxParty - 1;
                  return (
                    <button key={f.id} onClick={() => toggleGroupFriend(f.id)} disabled={full}
                      className={`w-full flex items-center gap-3 p-2.5 rounded-2xl border text-left transition-all ${sel ? "bg-fuchsia-500/20 border-fuchsia-400/50" : full ? "bg-white/[0.02] border-white/5 opacity-40" : "bg-white/5 border-white/10 hover:bg-white/10"}`}
                      data-testid={`party-friend-${f.id}`}>
                      <span className="relative w-9 h-9 rounded-full flex items-center justify-center text-lg border border-white/15">
                        <FriendFace friend={f} className="w-9 h-9 rounded-full flex items-center justify-center text-lg" style={{ background: "linear-gradient(135deg, #3B82F6, #8B5CF6)" }} />
                        {f.online && <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-slate-900" />}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-bold truncate">{f.display_name || f.username}</p>
                        {f.online && <p className="text-emerald-400 text-[10px] font-bold">● Online</p>}
                      </div>
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${sel ? "bg-fuchsia-500 border-fuchsia-400" : "border-white/20"}`}>{sel && <Check size={13} className="text-white" />}</span>
                    </button>
                  );
                })}
              </div>
            )}

            <button onClick={startParty} disabled={groupSending || groupSelected.length < 1}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white font-bold text-sm disabled:opacity-40 flex items-center justify-center gap-2"
              data-testid="party-start-btn">
              {groupSending ? <><Loader2 size={16} className="animate-spin" /> Setting up your party…</> : <><Gamepad2 size={16} /> Start Party ({groupSelected.length + 1} players)</>}
            </button>
            <p className="text-white/30 text-[11px] text-center mt-2">Friends get a tap-to-join invite — no PIN needed.</p>
          </div>
        </div>
      )}
    </>
  );
};

export default FriendProfileHost;
